import { GlossaryText } from '../../ui'
import styles from './AnalysisPage.module.css'

export interface ThresholdChartData {
  title: string
  unit: string
  vals: number[]
  threshold: number
  thr_label: string
  base: number | null
  base_label: string
  lo: number
  hi: number
  ticks: number[]
}

interface ThresholdChartProps {
  chart: ThresholdChartData
  days: string[]
  dangerStart: number
}

const width = 540
const height = 168
const margin = { left: 44, right: 12, top: 12, bottom: 26 }
const plotWidth = width - margin.left - margin.right
const plotHeight = height - margin.top - margin.bottom

export function ThresholdChart({ chart, days, dangerStart }: ThresholdChartProps) {
  const x = (index: number) => margin.left + plotWidth * index / (chart.vals.length - 1)
  const y = (value: number) => margin.top + plotHeight - plotHeight * (value - chart.lo) / (chart.hi - chart.lo)
  const linePath = chart.vals.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)} ${y(value).toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${x(chart.vals.length - 1).toFixed(1)} ${margin.top + plotHeight} L${margin.left} ${margin.top + plotHeight} Z`
  const latest = chart.vals.at(-1)

  return (
    <div className={styles.chart}>
      <div className={styles.chartTitle}>
        <b><GlossaryText text={chart.title} /></b>
        <span>近 {days.length} 天 · 最新 {chart.unit}{latest}</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
        {chart.ticks.map((tick) => {
          const tickY = y(tick)
          return (
            <g key={tick}>
              <line x1={margin.left} x2={width - margin.right} y1={tickY.toFixed(1)} y2={tickY.toFixed(1)} stroke="var(--divider)" strokeWidth="1" />
              <text x={margin.left - 6} y={(tickY + 4).toFixed(1)} textAnchor="end" fontSize="10.5" fill="var(--text-3)">{chart.unit}{tick}</text>
            </g>
          )
        })}
        {days.map((day, index) => (
          <text key={day} x={x(index).toFixed(1)} y={height - 8} textAnchor="middle" fontSize="10.5" fill="var(--text-3)">
            {day.replace('月', '/').replace('日', '')}
          </text>
        ))}
        <rect x={x(dangerStart).toFixed(1)} y={margin.top} width={(width - margin.right - x(dangerStart)).toFixed(1)} height={plotHeight} fill="var(--red-050)" opacity=".7" />
        {chart.base === null ? null : (
          <>
            <line x1={margin.left} x2={width - margin.right} y1={y(chart.base).toFixed(1)} y2={y(chart.base).toFixed(1)} stroke="var(--green-600)" strokeWidth="1.2" strokeDasharray="4 4" />
            <text x={margin.left + 6} y={(y(chart.base) - 5).toFixed(1)} textAnchor="start" fontSize="10.5" fill="var(--green-600)">{chart.base_label}</text>
          </>
        )}
        <line x1={margin.left} x2={width - margin.right} y1={y(chart.threshold).toFixed(1)} y2={y(chart.threshold).toFixed(1)} stroke="var(--red-600)" strokeWidth="1.2" strokeDasharray="4 4" />
        <text x={margin.left + 6} y={(y(chart.threshold) - 5).toFixed(1)} textAnchor="start" fontSize="10.5" fill="var(--red-600)">{chart.thr_label}</text>
        <path d={areaPath} fill="var(--blue-050)" opacity=".8" />
        <path d={linePath} fill="none" stroke="var(--blue-600)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        {chart.vals.map((value, index) => (
          <circle key={`${value}-${index}`} cx={x(index).toFixed(1)} cy={y(value).toFixed(1)} r="3.2" fill={value > chart.threshold ? 'var(--red-600)' : 'var(--blue-600)'} stroke="var(--surface)" strokeWidth="1.5" />
        ))}
      </svg>
      <div className={styles.legend}>
        <span><i className={styles.actualLine} />实际</span>
        <span className={styles.redLegend}><i className={styles.dashLine} />{chart.thr_label}</span>
        {chart.base === null ? null : <span className={styles.greenLegend}><i className={styles.dashLine} />{chart.base_label}</span>}
      </div>
    </div>
  )
}
